import React, { useCallback, useEffect, useState } from 'react'

import { useLocalizations } from '@vp/ubik-localizations-provider'

import type { RelatedProduct } from '../../../types/RelatedProduct'
import type { WorkId } from '../../../types/WorkId'
import { useProjects } from '../../root/contexts/ProjectsContext'
import { Mode, ProductTile } from '../../shared/ProductTile'
import { useCarouselScroll } from '../../root/contexts/CarouselScrollContext'
import { useRelatedProducts } from '../../root/contexts/RelatedProductsContext'
import { ProductCarousel } from '../../shared/ProductCarousel'
import { TrackingLabel } from '../../root/contexts/TrackingContext/TrackingConstants'
import { useTracking } from '../../root/contexts/TrackingContext'

export const RelatedProducts: React.FC = () => {
  const { t } = useLocalizations()
  const title = t('related-product.add-more-styles')
  const { addWorkId } = useProjects()
  const { relatedProductsCarouselRef, relatedProductsHeaderRef } = useCarouselScroll()
  const { filteredRelatedProducts, hasRelatedProductsToShow, isFetched } = useRelatedProducts()
  const { trackProductAdded, trackProductListViewed } = useTracking()

  const [productListTracked, setProductListTracked] = useState(false)

  useEffect(() => {
    if (isFetched && filteredRelatedProducts?.length && !productListTracked) {
      const productsToTrack = filteredRelatedProducts.map((product) => {
        return {
          mpvId: product.mpvId,
          productKey: product.productKey,
          productVersion: product.productVersion.toString(),
          productName: product.productName,
        }
      })
      trackProductListViewed(TrackingLabel.RELATED_PRODUCTS_CAROUSEL, productsToTrack)
      setProductListTracked(true)
    }
  }, [isFetched, filteredRelatedProducts])

  const runTrackingForProductAdded = useCallback(
    (product: RelatedProduct, position: number) => {
      trackProductAdded(TrackingLabel.RELATED_PRODUCT_ADDED, product.mpvId, product.productName, product.productKey, product.productVersion, position)
    },
    [trackProductAdded],
  )

  const handleAddProduct = useCallback(
    (workId: WorkId, product: RelatedProduct, position: number) => {
      product.workId = workId
      addWorkId(workId)
      runTrackingForProductAdded(product, position + 1)
    },
    [addWorkId, runTrackingForProductAdded],
  )

  const renderProductTiles = useCallback(
    (product: RelatedProduct, position: number) => {
      return (
        <ProductTile
          key={`related-product-${product.productKey}`}
          mode={Mode.RELATED_PRODUCT}
          product={product}
          position={position}
          onAddProduct={handleAddProduct}
        />
      )
    },
    [handleAddProduct],
  )

  return (
    <>
      {hasRelatedProductsToShow && filteredRelatedProducts && isFetched && (
        <ProductCarousel
          title={title}
          products={filteredRelatedProducts}
          renderProductTiles={renderProductTiles}
          emptyStateMessage={t('related-product.all-styles-added')}
          carouselRef={relatedProductsCarouselRef}
          headerRef={relatedProductsHeaderRef}
          testId="related-products-carousel"
        />
      )}
    </>
  )
}
